import { Search, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useAdminResource } from '../useAdminResource'
import { ORDER_LABELS, ORDER_STATUSES } from '../admin-orders'
import { AdminOrderTable, AdminTotalsStrip } from '../components/AdminOrderTable'
import type { AdminOrderPage } from '../types'

const PAGE_SIZE = 50

export function AdminOrders() {
  const [search, setSearch] = useState('')
  const [applied, setApplied] = useState('')
  const [statuses, setStatuses] = useState<string[]>([])
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [offset, setOffset] = useState(0)
  const [includeArchived, setIncludeArchived] = useState(false)

  const query = useMemo(() => {
    const params = new URLSearchParams()
    if (applied) params.set('q', applied)
    if (statuses.length) params.set('status', statuses.join(','))
    if (from) params.set('from', from)
    if (to) params.set('to', to)
    params.set('limit', String(PAGE_SIZE))
    params.set('offset', String(offset))
    params.set('includeArchived', String(includeArchived))
    return params.toString()
  }, [applied, statuses, from, to, offset, includeArchived])

  const { data: page, error, busy } = useAdminResource<AdminOrderPage>(`/api/admin/orders?${query}`)

  const toggleStatus = (value: string) => {
    setOffset(0)
    setStatuses(current => current.includes(value) ? current.filter(s => s !== value) : [...current, value])
  }
  const submitSearch = (event: React.FormEvent) => { event.preventDefault(); setOffset(0); setApplied(search.trim()) }
  const clearAll = () => { setSearch(''); setApplied(''); setStatuses([]); setFrom(''); setTo(''); setOffset(0) }

  const totals = page?.totals
  const filtered = Boolean(applied || statuses.length || from || to)
  const shownTo = page ? page.offset + page.orders.length : 0

  return (
    <section className="admin-orders page page--staff">
      <header className="page-head">
        <div>
          <div className="eyebrow"><span aria-hidden="true" />Admin</div>
          <h1>Orders</h1>
          <p>Follow each order from payment to delivery. Archived records stay out of this view unless you include them.</p>
        </div>
      </header>

      {totals && <AdminTotalsStrip totals={totals} />}

      <form className="admin-filters" onSubmit={submitSearch}>
        <div className="admin-search">
          <Search size={17} aria-hidden="true" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Order ID, RXID, tracking, customer name or email" aria-label="Search orders" />
          <button type="submit" className="button button--dark">Search</button>
        </div>
        <div className="admin-dates">
          <label>Orders created from<input type="date" value={from} onChange={e => { setOffset(0); setFrom(e.target.value) }} /></label>
          <label>Through (New York time)<input type="date" value={to} onChange={e => { setOffset(0); setTo(e.target.value) }} /></label>
          <label className="admin-checkbox"><input type="checkbox" checked={includeArchived} onChange={e => { setOffset(0); setIncludeArchived(e.target.checked) }} />Include archived history</label>
          {filtered && <button type="button" className="text-link" onClick={clearAll}><X size={14} />Clear</button>}
        </div>
        <div className="admin-chips" role="group" aria-label="Filter by status">
          {ORDER_STATUSES.map(status => {
            const count = page?.statusCounts[status] ?? 0
            return (
              <button key={status} type="button"
                className={`admin-chip ${statuses.includes(status) ? 'admin-chip--on' : ''}`}
                aria-pressed={statuses.includes(status)}
                onClick={() => toggleStatus(status)}>
                {ORDER_LABELS[status]}<i>{count}</i>
              </button>
            )
          })}
        </div>
      </form>

      {error && <div className="form-error" role="alert">{error}</div>}

      <div className="admin-table-wrap">
        <AdminOrderTable orders={page?.orders ?? []} />
        {!busy && page?.orders.length === 0 && (
          <p className="admin-empty">{filtered ? 'No orders match these filters.' : 'No orders yet.'}</p>
        )}
        {busy && <p className="admin-empty">Loading orders…</p>}
      </div>

      {totals && totals.count > 0 && (
        <nav className="admin-paging" aria-label="Pagination">
          <span>Showing {page!.offset + 1}–{shownTo} of {totals.count}</span>
          <button type="button" className="button button--outline" disabled={page!.offset === 0}
            onClick={() => setOffset(Math.max(0, page!.offset - PAGE_SIZE))}>Previous</button>
          <button type="button" className="button button--outline" disabled={shownTo >= totals.count}
            onClick={() => setOffset(page!.offset + PAGE_SIZE)}>Next</button>
        </nav>
      )}
    </section>
  )
}
